import React, {
  FunctionComponent,
  ComponentProps,
  ChangeEventHandler
} from 'react';
import { useEventCallback } from '../../hooks/useEventCallback';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { classes } from '../../utils/utils';
import styles from './Checkbox.module.css';

export type CheckboxProps = PropsWithoutChildren<ComponentProps<'label'>> & {
  label: string;
  value: boolean;
  onValueChanged: (value: boolean) => void;
  disabled?: boolean;
};

export const Checkbox: FunctionComponent<CheckboxProps> = React.memo(
  (props) => {
    const {
      label,
      value,
      onValueChanged,
      className,
      disabled,
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const handleChange: ChangeEventHandler<HTMLInputElement> = useEventCallback(
      (event) => {
        const newValue = event.currentTarget.checked;
        if (newValue !== value && !disabled) {
          onValueChanged(newValue);
        }
      }
    );

    return (
      <label
        {...remainingProps}
        className={classes(
          className,
          styles.label,
          disabled ? styles.disabled : null
        )}
      >
        <input
          id="any"
          name="any"
          type="checkbox"
          checked={value}
          onChange={handleChange}
          disabled={disabled}
          className={styles.input}
          data-testid="Checkbox.Input"
        />
        <span
          className={styles.labelText}
          data-testid="Checkbox.LabelText"
        >
          {label}
        </span>
      </label>
    );
  }
);
